import React from "react";
import classNames from "classnames";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import ColumnedInfoCard, { ColumnedInfoCardProps } from "./ColumnedInfoCard.blok";
import Button from "../../atoms/Button/Button.component";
import { ButtonProps } from "../../../interfaces/Button.interface";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";

interface ColumnedInfoSectionProps extends Blok {
  crumbs?: string;
  title?: string;
  description?: string;
  ctas?: ColumnedInfoCardProps[];
  button?: ButtonProps[];
  use_brand_background?: boolean;
}

const ColumnedInfoSection: React.FC<ColumnedInfoSectionProps> = ({
  crumbs,
  title,
  description,
  ctas = [],
  button = [],
  use_brand_background = false,
}) => {
  const isThreeUp = ctas.length === 3;

  return (
    <div className={classNames({ "bg-Primary-50": use_brand_background })}>
      <div className="container mx-auto py-12 md:py-16 lg:py-20">
        {/*-- header --*/}
        <div>
          {crumbs && (
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-Primary-500">{crumbs}</p>
          )}
          {title &&
            (use_brand_background ? (
              <h4 className="text-2xl font-extrabold text-SP6 md:text-3xl xl:text-4xl">{title}</h4>
            ) : (
              <h2 className="text-2xl font-extrabold text-Primary-900 md:text-3xl xl:text-4xl">{title}</h2>
            ))}
          {description && (
            <p className="mt-5 text-base font-semibold text-Primary-700 lg:text-lg">{description}</p>
          )}
        </div>

        {/*-- columns --*/}
        {!!ctas.length && (
          <ul
            className={classNames("mt-10 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:mt-12", {
              "lg:grid-cols-3": isThreeUp,
              "lg:grid-cols-4": !isThreeUp,
            })}
          >
            {ctas.map((cta: ColumnedInfoCardProps, index: number) => (
              <ColumnedInfoCard key={cta._uid ?? `columned-info-card--${index + 1}`} {...cta} />
            ))}
          </ul>
        )}

        {/*-- button --*/}
        {!!button.length && button[0].label && button[0].link && (
          <div className="mt-10 lg:mt-12">
            <Button
              link={button[0].link}
              label={button[0].label}
              type={ButtonTypes.PRIMARY}
              size={ButtonSizeTypes.LG}
              className="!text-base"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnedInfoSection;
